'use client';

import { useEffect, useRef } from 'react';
import { AD_SETTINGS } from '@/lib/adSlots';

interface TravelpayoutsWidgetProps {
  src: string;
  className?: string;
}

const EMERALD_SCRIPT_SELECTOR = 'script[data-tp-em]';
let emeraldLoadCount = 0;

function acquireEmeraldScript() {
  if (emeraldLoadCount === 0 && !document.head.querySelector(EMERALD_SCRIPT_SELECTOR)) {
    const script = document.createElement('script');
    script.async = true;
    script.setAttribute('data-cmp-ab', '2');
    script.src = `https://tp-em.com/${btoa(AD_SETTINGS.trs)}.js?t=${AD_SETTINGS.trs}`;
    script.dataset.tpEm = '1';
    // Silently handle CORS / network failures so they don't surface as
    // unhandled browser errors in Lighthouse / PageSpeed Insights audits.
    script.onerror = () => {
      // Emerald config unavailable – widgets may not render, but that's acceptable.
    };
    document.head.appendChild(script);
  }
  emeraldLoadCount += 1;
}

function releaseEmeraldScript() {
  emeraldLoadCount = Math.max(0, emeraldLoadCount - 1);
  if (emeraldLoadCount === 0) {
    document.head.querySelectorAll(EMERALD_SCRIPT_SELECTOR).forEach((node) => node.remove());
  }
}

export default function TravelpayoutsWidget({
  src,
  className = 'w-full',
}: TravelpayoutsWidgetProps) {
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) {
      return;
    }

    container.innerHTML = '';
    acquireEmeraldScript();

    const script = document.createElement('script');
    script.src = src;
    script.async = true;
    script.charset = 'utf-8';
    script.dataset.tpSrc = src;
    // Silently handle load failures (CORS block, 403, wrong MIME type) so
    // they don't appear as unhandled browser errors in Lighthouse/PSI audits.
    script.onerror = () => {
      // Widget unavailable – nothing to do, content area stays empty.
    };
    container.appendChild(script);

    return () => {
      container.querySelectorAll('script[data-tp-src]').forEach((node) => node.remove());
      container.innerHTML = '';
      releaseEmeraldScript();
    };
  }, [src]);

  return <div ref={containerRef} className={`relative z-0 ${className} min-h-[160px]`} />;
}
